import React, { useContext, useEffect, useMemo, useState } from "react";
import ReactHtmlParser from "react-html-parser";
import { useTranslation } from "react-i18next";
import { RestApiClientContext } from "src/hooks/context/pageContext";
import { Alert, AutoComplete, Divider, Form, Input, InputNumber, Modal, notification, Radio, Select, Tooltip } from "antd";
import { ButtonsCustom, ParagraphCustom, RadioCustom, SwitchCustomPro, TitleCustom } from "src/components/custom";
import { NO_CHART_REPITED, NO_CHART_REPITED_AND_NUMBER_DECIMAL, ONLY_NUMBER_D } from "src/constants/regex";
import LoadingOutlined from '@ant-design/icons/LoadingOutlined';
import "./academyItem.sass";
import { NewGroupSelect } from "src/components/advancedRetribution/newGroup.tsx/newGroupSelect";
import { TargetDto } from "src/models/targets/TargetModels";
import { LangContext } from "src/hooks/context/lang";

interface RetributionData {
    idAcademyGroups?: string;
    name: string;
    idSalesType?: string;
    isActive?: boolean;
    minNumberOfBadgets: number;
    academyGroupsMarkets?: any[],
    academyAgencies?: any[];
    academyGroupsAmount?: any[];
    academyBadges?: any[];
    academyGroupsChannels?: any[];

}

interface AcademyItem {
    data: {
        idAcademyGroups: string
        academyAgenciesSelected: any[],
        name: string
        isActive: boolean
        creationUser: string
        creationDate: string
        updateUser: any
        updateDate: any
        minNumberOfBadgets: number
        academyGroupsMarkets: any[]
        academyAgencies: any[]
        academyGroupsAmount: any[]
        academyBadges: any[]
        academyGroupsChannels: any[]

    },
    reload: () => void;
};
export type AgencySelected = { id: string, name: string, selected: boolean, address?: string };

export default function AcademyItem(props: AcademyItem) {
    const { t } = useTranslation(["advancedretributiontranslations"]);
    const { data, reload } = props;
    const language = useContext(LangContext);
    const userLang = language.GetLangContext();
    const restApiClient = useContext(RestApiClientContext);
    const [dataNumBadgets, setDataNumBadgets] = useState<number>(data?.minNumberOfBadgets === -1 ? null : data?.minNumberOfBadgets);
    const [dataMarket, setDataMarket] = useState<TargetDto[]>(data?.academyGroupsMarkets.filter((opt) => opt?.selected));
    const [dataAgencies, setDataAgencies] = useState<AgencySelected[]>(
        data?.academyAgenciesSelected
    );
    const [fieldsData, setFieldsData] = useState(
        data?.academyGroupsAmount?.reduce((a, v) => ({ ...a, [v?.idCurrency]: v?.amount }), {})
    );
    const [percentValue, setPercentValue] = useState(
        data?.academyGroupsAmount?.[0]?.amountType === "percent" ? data?.academyGroupsAmount?.[0]?.amount : null
    );
    const [academyBadges, setAcademyBadgets] = useState(data?.academyBadges.filter((opt) => opt?.selected));

    const [academyChannels, setAcademyChannels] = useState(data?.academyGroupsChannels?.filter((opt) => opt?.selected));

    const [radioValue, setRadioValue] = useState(data?.academyGroupsAmount?.[0]?.amountType === "percent" ? 1 : 2);
    const [currencyList, setCurrencyList] = useState(null);
    const [isActive, setIsActive] = useState(data?.isActive);
    const [form] = Form.useForm();
    const [showCoincidencesAlert, setShowCoincidencesAlert] = useState<boolean>(false);
    const [groupCoincidenceName, setGroupCoincidenceName] = useState<string>('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [savingData, setSavingData] = useState<boolean>(false);
    const academyBadgesIsSelectedData = useMemo(() => {
        return academyBadges.filter((opt) => opt?.selected).length > 0;
    }, [academyBadges]);
    const [agenciesList, setAgenciesList] = useState<any>([]);
    const [modalConfirmationDelete, setModalConfirmationDelete] = useState<boolean>(false);
    const [name, setName] = useState(
        data?.name
    );
    const getAgenciesList = (name: string, results = 20) => {
        const filter = { page: 1, resultPage: results, name: name, language: userLang, address: "" };
        restApiClient.getAgencies(filter).then((data) => {
            setAgenciesList(data);
        });
    };
    const onSearch = async (searchText, results = 20) => {
        await getAgenciesList(searchText, results);
    };
    const handleSave = async () => {
        const body: RetributionData = {
            idAcademyGroups: data?.idAcademyGroups,
            name: name,
            isActive: isActive,
            minNumberOfBadgets: dataNumBadgets === null ? -1 : dataNumBadgets,
            academyGroupsMarkets: dataMarket,
            academyAgencies: dataAgencies,
            academyBadges: academyBadges,
            academyGroupsChannels: academyChannels,
            academyGroupsAmount:
                radioValue === 2
                    ? Object.keys(fieldsData)
                        ?.filter((curr) => fieldsData?.[curr] && curr !== "percent")
                        .map((curr) => ({
                            amount: fieldsData[curr],
                            idAcademyGroups: data?.idAcademyGroups,
                            idCurrency: curr,
                            amountType: "fixed",
                        }))
                    : [
                        {
                            amount: percentValue,
                            idAcademyGroups: data?.idAcademyGroups,
                            idCurrency: null,
                            amountType: "percent"
                        }
                    ]
        };

        if (!body?.academyGroupsAmount
            || body?.academyGroupsAmount.length == 0
            || body?.academyGroupsAmount.some(value => value.amount === undefined || value.amount === null || value.amount === "")
        ) {
            notification.error({
                message: t("empty.no-amounts"),
                description: '',
            })
        } else {
            setSavingData(true);
            restApiClient.updateAcademyConfiguration(data?.idAcademyGroups, body).finally(() => {
                setSavingData(false);
            })
        }


    };

    const handleCheckCoincidences = async () => {
        const body: RetributionData = {
            idAcademyGroups: data?.idAcademyGroups,
            name: name,
            isActive: isActive,
            academyGroupsMarkets: dataMarket,
            minNumberOfBadgets: dataNumBadgets,
            academyAgencies: dataAgencies,
            academyBadges: academyBadges,
            academyGroupsChannels: academyChannels,
            academyGroupsAmount:
                radioValue === 2
                    ? Object.keys(fieldsData)
                        ?.filter((curr) => fieldsData?.[curr])
                        .map((curr) => ({
                            amount: fieldsData[curr] || 0,
                            idAcademyGroups: data?.idAcademyGroups,
                            idCurrency: curr,
                            amountType: "fixed"
                        }))
                    : [
                        {
                            amount: percentValue || 0,
                            idAcademyGroups: data?.idAcademyGroups,
                            idCurrency: null,
                            amountType: "percent"
                        }
                    ]
        };

        restApiClient.checkCoincidencesAcademyConfigurations(data?.idAcademyGroups, body).then((data: any) => {
            if (data && data.length > 0) {
                setGroupCoincidenceName(data);
                setShowCoincidencesAlert(true);
            } else {
                setGroupCoincidenceName('');
                setShowCoincidencesAlert(false);
            }
        });
    };

    const handleOnChange = (e) => {
        setFieldsData({ ...fieldsData, [e[0].name]: e[0].value });
        form.setFieldsValue({ ...fieldsData, [e[0].name]: e[0].value });
    };

    const [removing, setRemoving] = useState<boolean>(false);
    const handleOnDelete = () => {
        setRemoving(true);
        setModalConfirmationDelete(false);
        restApiClient.deleteAcademyConfiguration(data?.idAcademyGroups).then((r: any) => {
            reload();
        }).finally(() => {
            setRemoving(false);
        });
    }

    const convert = async () => {
        try {
            currencyList.forEach(async (curr, i) => {
                if (i) {
                    const conversion = {
                        currencyFrom: currencyList[0],
                        amount: fieldsData[currencyList[0]],
                        currencyTo: curr
                    };
                    const currencyConverted = await restApiClient.getCurrencyConversion(conversion);
                    setFieldsData((prevData) => ({ ...prevData, [curr]: currencyConverted }));
                    form.setFieldsValue({ ...fieldsData, [curr]: currencyConverted });
                }
            });
        } catch (e) {
        }
    };

    useEffect(() => {
        restApiClient.getListCurrency().then((data) => setCurrencyList(data));

    }, []);


    const handleChangeActiveStatus = (isActiveNewStatus: boolean) => {
        setSavingData(true);
        restApiClient.changeAcademyConfigurationStatus(data?.idAcademyGroups, isActiveNewStatus).finally(() => { setSavingData(false) });
    }


    return (

        <div id="new-group" className="row">
            <div className="button-container box w-100">
                <div style={{ alignItems: 'center' }} className="title-buttons d-flex justify-content-between">
                    <p style={{ margin: '0', whiteSpace: 'nowrap', marginRight: '10px' }}> <strong>{t('group.title')}</strong> </p>
                    <Input
                        style={{ width: '100%', marginRight: '20px' }}
                        placeholder={t("group.title")}
                        value={name}
                        onChange={(e) => {
                            setName(e.target.value)
                            handleCheckCoincidences()
                        }}
                    />
                    <div className="d-flex buttons justify-content-end align-items-center">
                        <SwitchCustomPro checked={isActive} onChange={(checked: any) => { handleChangeActiveStatus(checked); setIsActive((prev) => !prev); }} />
                        <ButtonsCustom
                            htmlType="button"
                            className="btn-primary"
                            type="primary"
                            disabled={savingData}
                            onClick={handleSave}
                            content={<span>{savingData ? <LoadingOutlined /> : ''} {t("group.save")}</span>}
                        />
                        <ButtonsCustom
                            className="btn-secondary"
                            htmlType="submit"
                            onClick={() => setModalConfirmationDelete(true)}
                            disabled={removing}
                            content={<span>{removing ? <LoadingOutlined /> : ''} {t("group.delete")}</span>}
                        />
                    </div>
                </div>
                {
                    showCoincidencesAlert ?
                        <div className="d-flex">
                            <div className="col selects-col">
                                <Alert
                                    className="warn-academy-group-coincidences"
                                    message={ReactHtmlParser(t("group.coincidences", { 0: groupCoincidenceName }))}

                                    type="warning"
                                />
                            </div>
                        </div>
                        : ''
                }
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '20px', width: '100%' }}>
                    <div style={{ gap: '15px' }} className="d-flex">
                        <NewGroupSelect
                            style={{ width: '100%' }}
                            title={t("group.market")}
                            options={data?.academyGroupsMarkets}
                            placeholder={t("group.market-placeholder")}
                            setData={setDataMarket}
                            data={dataMarket}
                            onChange={handleCheckCoincidences}
                        />
                        <div style={{ width: '100%' }} id="new-group-select" >
                            <div className="title-img  justify-content-between">
                                <NewGroupSelect
                                    style={{ width: '100%' }}
                                    title={t("group.channel")}
                                    options={data?.academyGroupsChannels}
                                    placeholder={t("group.channel")}
                                    setData={setAcademyChannels}
                                    data={academyChannels}
                                    onChange={handleCheckCoincidences}

                                />
                            </div>
                        </div>
                    </div>

                    <div style={{ gap: '15px' }} className="d-flex">

                        <NewGroupSelect
                            style={{ width: '100%' }}
                            title={t("group.agencies")}
                            isValueFormated={false}
                            useToolTip={true}
                            options={agenciesList?.agencies ? agenciesList?.agencies.map((opt) => ({ id: opt.id, name: opt.name, selected: true, address: opt.address } as AgencySelected)) : []}
                            onSearch={(e) => onSearch(e, 20)}
                            placeholder={t("group.agencies-placeholder")}
                            setData={
                                setDataAgencies
                            }
                            data={dataAgencies}
                            onChange={handleCheckCoincidences}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}

                        />
                        <div style={{ width: '100%' }}>
                            <div className="title-img  justify-content-between">
                                <NewGroupSelect
                                    style={{ width: '100%' }}
                                    title={t("group.badge")}
                                    options={data?.academyBadges}
                                    placeholder={t("group.badge")}
                                    setData={setAcademyBadgets}
                                    data={academyBadges}
                                    onChange={handleCheckCoincidences}

                                />
                            </div>
                            <div id="new-group-select" className="my-3">
                                <div className="title-img  justify-content-between">
                                    <TitleCustom level={3} class="title-group-select" text={t("group.num-badges") + (academyBadgesIsSelectedData ? ` (${t("group.use-badge")})` : '')} />
                                    <InputNumber
                                        disabled={academyBadgesIsSelectedData}
                                        title={t("group.num-badges")}
                                        placeholder={t("group.room-placeholder")}
                                        value={dataNumBadgets}
                                        onChange={(e) => {
                                            setDataNumBadgets(e)
                                            handleCheckCoincidences()
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
                <Form
                    className="d-flex flex-column"
                    onFinish={handleSave}
                    form={form}
                    layout="vertical"
                    id="edit-channel"
                    onFieldsChange={handleOnChange}
                    initialValues={fieldsData}
                >
                    <TitleCustom level={3} class="title-group-section" text={t("group.increments")} />
                    <ParagraphCustom text={t("group.explained-academy")} />
                    <Radio.Group onChange={(e) => setRadioValue(e.target.value)} value={radioValue} className="radios d-flex">
                        <div className="d-flex flex-column">
                            <div className="radio-title d-flex flex-row">
                                <RadioCustom value={1} />
                                <ParagraphCustom text={t("group.percent")} />
                            </div>
                            <Form.Item name="percent">
                                <Input
                                    suffix="%"
                                    onChange={(e) => setPercentValue(e.target.value)}
                                    value={percentValue}
                                    defaultValue={percentValue}
                                    disabled={radioValue !== 1 ? true : false}
                                />
                            </Form.Item>
                        </div>
                        <div className="d-flex flex-column">
                            <div className="radio-title d-flex flex-row ">
                                <RadioCustom value={2} />
                                <ParagraphCustom text={t("group.fixed")} />
                            </div>
                            <div className="inputs d-flex">
                                {
                                    <Form.Item
                                        name={currencyList?.[0]}
                                        rules={[
                                            {
                                                validator: (_, value) =>
                                                    value
                                                        ? ONLY_NUMBER_D.test(value)
                                                            ? NO_CHART_REPITED.test(value)
                                                                ? NO_CHART_REPITED_AND_NUMBER_DECIMAL.test(value)
                                                                    ? Promise.resolve()
                                                                    : Promise.reject(t("reward:modal.validations.num-decimals"))
                                                                : Promise.reject(t("reward:modal.validations.formated"))
                                                            : Promise.reject(t("reward:modal.validations.number"))
                                                        : Promise.resolve()
                                            }
                                        ]}
                                    >
                                        <InputNumber
                                            prefix={currencyList?.[0]}
                                            disabled={radioValue !== 2 ? true : false}
                                            decimalSeparator={","}
                                        />
                                    </Form.Item>
                                }
                                <ButtonsCustom
                                    content={"Convertir"}
                                    className="btn-primary"
                                    type="primary"
                                    onClick={() => convert()}
                                    disabled={radioValue !== 2 ? true : false}
                                />
                                {currencyList &&
                                    currencyList.map((val, i) =>
                                        i ? (
                                            <Form.Item name={val} key={val}>
                                                <InputNumber
                                                    prefix={val}
                                                    key={i}
                                                    disabled={radioValue !== 2 ? true : false}
                                                    decimalSeparator={","}
                                                />
                                            </Form.Item>
                                        ) : (
                                            ""
                                        )
                                    )}
                            </div>
                        </div>
                    </Radio.Group>
                </Form>
            </div>
            <Modal title={t("group.delete-message")} visible={modalConfirmationDelete} onOk={() => handleOnDelete()} onCancel={() => setModalConfirmationDelete(false)}>

            </Modal>
        </div>


    );
};
